import axios from 'axios';
import moment from 'moment';

// Attach the logged-in user's JWT (set by Login.js) to every request. The
// backend's ClientDbFilter reads client_id from this Bearer token to point
// the `client` DB group at the right client's database — without it (e.g.
// cross-origin in production, where the PHP session cookie never reaches the
// API), every request silently falls back to whatever client DB is hardcoded
// in the backend's .env, and report data comes back empty/wrong.
axios.interceptors.request.use((config) => {
    try {
        const token = window?.localStorage?.getItem('token');
        if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch {
        // ignore storage access errors (private mode, SSR, etc.)
    }
    return config;
});

// In dev (npm start), use a relative path so requests go through the CRA proxy
// and avoid CORS. In production (Electron file://), use the absolute URL.
const BASE_URL = process.env.NODE_ENV === 'development' ? '/Counter-Backend' : 'https://gsonlinesolutions.com/Counter-Backend/';

const normalizeBaseUrl = (url) => String(url || '').replace(/\/+$/, '');
export const getSalesReactBaseUrl = () => {
    // In development, always use the local proxy — never read from localStoragecls
    if (process.env.NODE_ENV === 'development') return normalizeBaseUrl(BASE_URL);
    try {
        if (typeof window !== 'undefined' && window?.localStorage) {
            // When running in a browser (not Electron), always use the server BASE_URL.
            // localStorage.base_url is only relevant for Electron desktop clients
            // that connect to a local backend — it must not override web deployments.
            const isElectron = window.location?.protocol === 'file:';
            if (!isElectron) return normalizeBaseUrl(BASE_URL);

            const role = String(window.localStorage.getItem('role') || '')
                .trim()
                .toUpperCase();
            if (role === 'GSPOS') return normalizeBaseUrl(BASE_URL);
            const stored = window.localStorage.getItem('base_url');
            if (stored) return normalizeBaseUrl(stored);
        }
    } catch {
        // ignore storage access errors (private mode, SSR, etc.)
    }
    return normalizeBaseUrl(BASE_URL);
};

const SALES_MASTER_BASE_URL = BASE_URL;
const SALES_MASTER_ADMIN_BASE_URL = BASE_URL;

/**
 * Resolve a stored file path (e.g. voucher attachment) into a URL that can be
 * used directly in an <img>/<a> tag.
 * @param {string} path - Absolute URL, or a path/filename returned by the API
 * @returns {string} - Full URL, or '' if no path was given
 */
export const resolveUploadUrl = (path) => {
    const str = String(path || '').trim();
    if (!str) return '';
    if (/^(https?:)?\/\//i.test(str) || str.startsWith('data:')) return str;
    const base = getSalesReactBaseUrl();
    const cleanPath = str.replace(/^\/+/, '');
    if (cleanPath.startsWith('uploads/')) return `${base}/${cleanPath}`;
    return `${base}/uploads/vouchers/${cleanPath}`;
};

/**
 * Fetch bill filter header data (branches and statuses)
 * @param {Object} payload - Request payload
 * @param {string} payload.rolename - Role name (e.g., 'CEO', 'admin', 'manager')
 * @param {string} payload.clientname - Client ID
 * @returns {Promise<Object>} - { branchs: [], status: [] }
 */
export const getBillFilterHeader = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_billfillterheader`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching bill filter header:', error);
        throw error;
    }
};

/**
 * Fetch branches list
 * @param {string} clientId - Client ID
 * @returns {Promise<Array>} - Array of branches
 */
export const getBranches = async (clientId) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/branches`, { params: { client_id: clientId } });
        return response.data || [];
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
};

/**
 * Fetch dynamic user menu for sidebar
 * @param {string|number} userId - Logged-in user id
 * @returns {Promise<Array>} - Menu groups array
 */
export const getUserMenu = async (userId) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/user_menu/${userId}`);
        return response.data || [];
    } catch (error) {
        console.error('Error fetching user menu:', error);
        throw error;
    }
};

/**
 * React login call
 * @param {Object} payload
 * @param {string} payload.username
 * @param {string} payload.password
 */
export const loginReact = async ({ username, password }) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/reactLogin`, { username, password });
        return response.data;
    } catch (error) {
        console.error('Error during react login:', error);
        throw error;
    }
};

/**
 * React logout call
 * @param {Object} payload
 * @param {string|number} payload.user_id - ID of the user logging out
 */
export const logoutReact = async (payload = {}) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/login/logout`, payload);
        return response.data;
    } catch (error) {
        console.error('Error during logout:', error);
        throw error;
    }
};

/**
 * Fetch bill list data (old API - deprecated)
 * @param {Object} params - Query parameters
 * @param {string} params.from_date - From date (YYYY-MM-DD)
 * @param {string} params.to_date - To date (YYYY-MM-DD)
 * @param {string} params.branch_id - Branch ID (optional)
 * @param {string} params.status - Status filter (optional)
 * @returns {Promise<Array>} - Array of bill data
 */
// export const getBillList = async (params) => {
//     try {
//         const response = await axios.get(`${SALES_MASTER_BASE_URL}/SalesApi/get_bill_list`, { params });
//         return response.data || [];
//     } catch (error) {
//         console.error('Error fetching bill list:', error);
//         throw error;
//     }
// };

/**
 * Filter bills list (new API)
 * @param {Object} payload - Request payload
 * @param {string} payload.from_date - From date (YYYY-MM-DD)
 * @param {string} payload.to_date - To date (YYYY-MM-DD)
 * @param {string} payload.branch_id - Branch ID (optional)
 * @param {string} payload.status - Status filter (optional)
 * @returns {Promise<Array>} - Array of bill data
 */
export const filterBillsList = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/filtter_billslist`, payload);
        return response.data?.bills || [];
    } catch (error) {
        console.error('Error fetching filtered bills list:', error);
        throw error;
    }
};

/**
 * Distinct customer names (cust_name) for CREDIT bills, used by the Credit Report customer filter
 * @param {Object} payload - Request payload
 * @param {string} payload.branch_id - Branch ID (optional, "All" or empty for all branches of the client)
 * @param {string} payload.client_id - Client ID (used when branch_id is empty/"All")
 * @param {boolean} [payload.include_settled] - Also list customers whose credit bills are already settled
 * @returns {Promise<Array>} - Array of { cust_name }
 */
export const getCreditCustomers = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_credit_customers`, payload);
        return response.data?.customers || [];
    } catch (error) {
        console.error('Error fetching credit customers:', error);
        throw error;
    }
};

/**
 * List all clients (server database), used by the Excel Export page's client picker (GSPOS role only)
 * @returns {Promise<Array>} - Array of client_creation rows
 */
export const getClientsList = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/server_masters/list/client_creation`);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching clients list:', error);
        throw error;
    }
};

/**
 * Excel Export endpoints — Counter section (Masters: server DB, Reports: client DB)
 * and Orders section (client DB, Institution + ODC combined). Each returns { data: [...] }.
 */
const exportPost = async (path, payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/export/${path}`, payload);
        return response.data?.data || [];
    } catch (error) {
        console.error(`Error fetching export data (${path}):`, error);
        throw error;
    }
};

export const exportCounterProducts = (payload) => exportPost('counter_products', payload);
export const exportCounterCategories = (payload) => exportPost('counter_categories', payload);
export const exportCounterItemReport = (payload) => exportPost('counter_item_report', payload);
export const exportCounterCategoryReport = (payload) => exportPost('counter_category_report', payload);

export const exportOrdersProducts = (payload) => exportPost('orders_products', payload);
export const exportOrdersCategories = (payload) => exportPost('orders_categories', payload);
export const exportOrdersCustomersProducts = (payload) => exportPost('orders_customers_products', payload);
export const exportOrdersItemReport = (payload) => exportPost('orders_item_report', payload);
export const exportOrdersCategoryReport = (payload) => exportPost('orders_category_report', payload);
export const exportOrdersCustomerReport = (payload) => exportPost('orders_customer_report', payload);

/**
 * Advance (online) bill report - filter header
 * @param {Object} payload - { rolename, clientname }
 */
export const getBillFilterHeaderOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_billfillterheader_online`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching advance bill filter header:', error);
        throw error;
    }
};

/**
 * Advance (online) bill report - filtered bills list
 * @param {Object} payload - { from_date, to_date, branch_id, status }
 */
export const filterBillsListOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/filtter_billslist_online`, payload);
        return response.data?.bills || [];
    } catch (error) {
        console.error('Error fetching advance filtered bills list:', error);
        throw error;
    }
};

/**
 * Helper function to format date for API calls
 * @param {Date} date - Date object
 * @returns {string} - Formatted date string (YYYY-MM-DD)
 */
export const formatDateForApi = (date) => {
    return moment(date).format('YYYY-MM-DD');
};

/**
 * Fetch sales report data by date range and branch
 * @param {Object} payload - Request payload
 * @param {string} payload.from_date - From date (YYYY-MM-DD)
 * @param {string} payload.to_date - To date (YYYY-MM-DD)
 * @param {string} payload.branch_id - Branch ID or "All"
 * @returns {Promise<Object>} - Object with bills array and summary object
 */
export const getSalesReportData = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/payment_bydaterannge`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching sales report data:', error);
        throw error;
    }
};

/**
 * Fetch Sales Reporta data (closed bills only, pivoted by bill final_mop).
 * @param {Object} payload - { from_date, to_date, branch_id, client_id? }
 * @returns {Promise<Object>} - Object with bills array and summary object
 */
export const getSalesReportaData = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/payment_bydaterannge_closed`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching sales reporta data:', error);
        throw error;
    }
};

/**
 * Fetch hourly sales report by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, data: { "DD-MM-YYYY": [ { time_range, Total_Hourly_Sales, payments } ], ... } }
 */
export const getHourlySalesReport = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/payment_bysaleshoulry`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching hourly sales report:', error);
        throw error;
    }
};

/**
 * Fetch category-wise report by date range and branch
 * @param {Object} payload - Request payload
 * @param {string} payload.from_date - From date (YYYY-MM-DD)
 * @param {string} payload.to_date - To date (YYYY-MM-DD)
 * @param {string} payload.branch_id - Branch ID or "All"
 * @returns {Promise<Object>} - Category data object keyed by category name with hourly arrays
 */
export const getCategoryReportByDateRange = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_daterange`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category report:', error);
        throw error;
    }
};

/**
 * Fetch category-wise hourly sales report by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, data } (data keyed by category or report format from API)
 */
export const getCategoryReportHourly = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_hourly`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category hourly report:', error);
        throw error;
    }
};

/**
 * Fetch category-wise tax report by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, timestamp, data: [] }
 */
export const getCategoryReportTaxwise = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_taxwise`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category tax-wise report:', error);
        throw error;
    }
};

/**
 * Fetch category performance ranking (top/bottom selling) by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, report_period, top_selling: [], bottom_selling: [] }
 */
export const getCategoryReportTopBottomSelling = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_topbottomselling`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category performance ranking report:', error);
        throw error;
    }
};

/**
 * Category Online Report - Full report by date range
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getCategoryReportByDateRangeOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_daterange_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category online report:', error);
        throw error;
    }
};

/**
 * Category Online Report - Hourly sales
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getCategoryReportHourlyOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_hourly_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category online hourly report:', error);
        throw error;
    }
};

/**
 * Category Online Report - Tax-wise sales
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getCategoryReportTaxwiseOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_taxwise_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category online tax-wise report:', error);
        throw error;
    }
};

/**
 * Category Online Report - Performance (top/bottom selling)
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getCategoryReportTopBottomSellingOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_categoryreport_topbottomselling_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching category online performance ranking report:', error);
        throw error;
    }
};

/**
 * Item Report - Full report by date range
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, bills: [], summary: {} }
 */
export const getItemReportByDateRange = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_daterange`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item report by date range:', error);
        throw error;
    }
};

/**
 * Item Report - Hourly sales
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportHourly = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_hourly`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item hourly report:', error);
        throw error;
    }
};

/**
 * Item Report - Tax-wise sales
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportTaxwise = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_taxwise`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item tax-wise report:', error);
        throw error;
    }
};

/**
 * Item Report - Performance (top/bottom selling)
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportTopBottomSelling = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_topbottomselling`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item performance report:', error);
        throw error;
    }
};

/**
 * Item Online Report - Full report by date range
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportByDateRangeOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_daterange_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item online report by date range:', error);
        throw error;
    }
};

/**
 * Item Online Report - Hourly sales
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportHourlyOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_hourly_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item online hourly report:', error);
        throw error;
    }
};

/**
 * Item Online Report - Tax-wise sales
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportTaxwiseOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_taxwise_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item online tax-wise report:', error);
        throw error;
    }
};

/**
 * Item Online Report - Performance (top/bottom selling)
 * @param {Object} payload - { from_date, to_date, branch_id }
 */
export const getItemReportTopBottomSellingOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/get_itemreport_topbottomselling_online`, payload);
        const errMsg = response.data?.error || response.data?.message;
        if (errMsg) throw new Error(errMsg);
        return response.data;
    } catch (error) {
        console.error('Error fetching item online performance report:', error);
        throw error;
    }
};

/**
 * Fetch user-wise sales report by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, data: { "DD-MM-YYYY": { "User ID: N": { user_daily_total, shifts } }, ... } }
 */
export const getUserWiseSalesReport = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/payment_bysalesuser`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching user-wise sales report:', error);
        throw error;
    }
};

/**
 * Fetch waiter-wise sales report by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id }
 * @returns {Promise<Object>} - { status, data: { "DD-MM-YYYY": { "Waiter": { user_daily_total, shifts } }, ... } }
 */
export const getWaiterSalesReport = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/payment_bywaiter`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching waiter sales report:', error);
        throw error;
    }
};

/**
 * Fetch receipt details for a bill
 * @param {string} billId - Bill ID
 * @param {string} branchId - Branch ID
 * @returns {Promise<Object>} - Receipt data
 */
export const getReceiptDetails = async (billId, branchId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/counter/getreceiptdetails/${billId}/${branchId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching receipt details:', error);
        throw error;
    }
};

/**
 * Cancel a bill with reason
 * @param {string} billId - Bill ID
 * @param {string} branchId - Branch ID
 * @param {string} remarks - Cancellation reason/remarks
 * @param {string} editedBy - User who is cancelling the bill
 * @returns {Promise<Object>} - Response
 */
export const cancelBill = async (billId, branchId, remarks, editedBy) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/cancel_billdetails`, {
            bill_id: billId,
            branch_id: branchId,
            remarks,
            edited_by: editedBy
        });
        return response.data;
    } catch (error) {
        console.error('Error cancelling bill:', error);
        throw error;
    }
};

/**
 * Update bill payment mode (e.g. from CASH to UPI)
 * @param {string} billId - Bill ID
 * @param {string} branchId - Branch ID
 * @param {string} payMode - New payment mode (setting_name from receipt_settings)
 * @param {string} editedBy - User who is changing the payment mode
 * @param {string} [remarks] - Reason/remarks for the change
 * @returns {Promise<Object>} - Response
 */
export const updateBillPaymentMode = async (billId, branchId, payMode, editedBy, remarks) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/paymentmode_change`, {
            bill_id: billId,
            branch_id: branchId,
            pay_mode: payMode,
            edited_by: editedBy,
            remarks
        });
        return response.data;
    } catch (error) {
        console.error('Error updating bill payment mode:', error);
        throw error;
    }
};

/**
 * Settle a credit bill (payment callback)
 * @param {Object} payload - { bill_id, branch_id, payment_type, amount, remarks, settlement_date (YYYY-MM-DD), settlement_by }
 * @returns {Promise<*>} - API response (often array with { status: 'success' })
 */
export const settleCreditBill = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/settelement_billdetails`, payload);
        return response.data;
    } catch (error) {
        console.error('Error settling bill:', error);
        throw error;
    }
};

/**
 * Advance (online) bill report - receipt details
 */
export const getReceiptDetailsOnline = async (billId, branchId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/counter/getreceiptdetails_online/${billId}/${branchId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching advance receipt details:', error);
        throw error;
    }
};

/**
 * Advance (online) bill report - cancel bill
 */
export const cancelBillOnline = async (billId, branchId, remarks, editedBy) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/cancel_billdetails_online`, {
            bill_id: billId,
            branch_id: branchId,
            remarks,
            edited_by: editedBy
        });
        return response.data;
    } catch (error) {
        console.error('Error cancelling advance bill:', error);
        throw error;
    }
};

/**
 * Advance (online) bill report - update payment mode
 */
export const updateBillPaymentModeOnline = async (billId, branchId, payMode, editedBy) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/paymentmode_change_online`, {
            bill_id: billId,
            branch_id: branchId,
            pay_mode: payMode,
            edited_by: editedBy
        });
        return response.data;
    } catch (error) {
        console.error('Error updating advance bill payment mode:', error);
        throw error;
    }
};

/**
 * Advance (online) bill report - settle credit bill
 */
export const settleCreditBillOnline = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/payment_callback_online`, payload);
        return response.data;
    } catch (error) {
        console.error('Error settling advance bill:', error);
        throw error;
    }
};

/**
 * Fetch institutional filter data (companies, categories, products)
 * @param {Object} payload - Request payload
 * @param {string} payload.branch_id - Branch ID
 * @returns {Promise<Object>} - { company: [], category: [], products: [] }
 */
export const getInstitutionalFilter = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/get_institutionalfiltter`, payload);
        return response.data || { company: [], category: [], products: [] };
    } catch (error) {
        console.error('Error fetching institutional filter:', error);
        throw error;
    }
};

/**
 * Insert new institutional order
 * @param {Object} payload - Order payload
 * @returns {Promise<Object>} - Response with status and bill_no
 */
export const insertInstitutionalOrder = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/insertNewinstituteOrder`, payload);
        return response.data;
    } catch (error) {
        console.error('Error inserting institutional order:', error);
        throw error;
    }
};

/**
 * Fetch order by ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - { header: {}, items: [] }
 */
export const getOrderById = async (orderId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/institute/getOrderById/${orderId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching order by ID:', error);
        throw error;
    }
};

/**
 * Update order cart
 * @param {Object} payload - Order update payload
 * @returns {Promise<Object>} - Response with status
 */
export const updateOrderCart = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/updateOrderCart`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating order cart:', error);
        throw error;
    }
};

/**
 * Fetch institute list with branches and bills
 * @param {Object} payload - Request payload
 * @param {string} payload.rolename - Role name
 * @param {string} payload.clientname - Client ID
 * @returns {Promise<Object>} - { branchs: [], bills: [] }
 */
export const getInstituteList = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/get_institutelist`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching institute list:', error);
        throw error;
    }
};

/**
 * Filter institute list by status, date, branch
 * @param {Object} payload - Request payload
 * @param {string} payload.status - Status filter (pending, dispatched, settled, cancelled)
 * @param {string} payload.from_date - From date
 * @param {string} payload.to_date - To date
 * @param {string} payload.branch_id - Branch ID
 * @returns {Promise<Object>} - { bills: [] }
 */
export const filterInstituteList = async (payload) => {
    try {
        let endpoint = `${getSalesReactBaseUrl()}/api/institute/filtter_institutelist`;
        if (payload.status === 'dispatched') endpoint = `${getSalesReactBaseUrl()}/api/institute/filter_dispatched_list`;
        else if (payload.status === 'settled') endpoint = `${getSalesReactBaseUrl()}/api/institute/filter_settled_list`;
        else if (payload.status === 'cancelled') endpoint = `${getSalesReactBaseUrl()}/api/institute/filter_cancelled_list`;

        const response = await axios.post(endpoint, payload);
        return response.data;
    } catch (error) {
        console.error('Error filtering institute list:', error);
        throw error;
    }
};

/**
 * Fetch dispatch details for an order
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - Dispatch info
 */

/**
 * Update dispatch status for an order
 * @param {Object} payload - Dispatch payload
 * @param {string} payload.order_id - Order ID
 * @param {string} payload.status - New status
 * @param {string} payload.rolename - User name
 * @param {string} payload.branch_id - Branch ID
 * @param {string} payload.branchname - Branch name
 * @returns {Promise<Object>} - Response with status
 */
export const updateDispatchStatus = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/updateDispatchStatus`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating dispatch status:', error);
        throw error;
    }
};

/**
 * Expense types for voucher dropdown (adjust response shape to match your API).
 * @param {Object} payload - e.g. { client_id }
 */
export const getVoucherExpenses = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/get_expansesdetails`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching voucher expenses:', error);
        throw error;
    }
};

export const createVoucher = async (payload) => {
    try {
        const isFormData = payload instanceof FormData;
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/cash_vocherentry`, payload, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {});
        return response.data;
    } catch (error) {
        console.error('Error creating voucher:', error);
        throw error;
    }
};

/**
 * Submit a "Report Issue" complaint: an annotated screenshot + description + page context.
 * @param {FormData} payload - screenshot (File/Blob), description, page_name, page_path,
 *   reported_by_userid, reported_by_username, reported_by_role, branch_id, branch_name,
 *   client_id, reported_at, user_agent, screen_resolution
 */
export const submitIssueReport = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/submit_report`, payload, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data;
    } catch (error) {
        console.error('Error submitting issue report:', error);
        throw error;
    }
};

/**
 * List raised issue reports. GSPOS sees every report; every other role only sees
 * reports they personally raised (backend scopes by rolename + userid).
 * @param {Object} payload - { rolename, userid, status, client_id, branch_id, date_from, date_to, search }
 */
export const listIssueReports = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/list_reports`, payload);
        return response.data;
    } catch (error) {
        console.error('Error listing issue reports:', error);
        throw error;
    }
};

/**
 * Change the status of a raised issue report (tracked with who/when).
 * @param {Object} payload - { id, status, updated_by }
 */
export const updateIssueReportStatus = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/update_status`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating issue report status:', error);
        throw error;
    }
};

/**
 * List GSPOS-role staff who a raised issue can be assigned to.
 */
export const getAssignableEmployees = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/support/list_assignable_employees`);
        return response.data;
    } catch (error) {
        console.error('Error listing assignable employees:', error);
        throw error;
    }
};

/**
 * Assign a raised issue to a staff member and/or set its priority.
 * @param {Object} payload - { id, priority, assigned_to_userid, assigned_to_username, assigned_by }
 */
export const assignIssueReport = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/assign_issue`, payload);
        return response.data;
    } catch (error) {
        console.error('Error assigning issue report:', error);
        throw error;
    }
};

/** Start the work-tracking clock on an issue. @param {Object} payload - { issue_report_id, started_by } */
export const startIssueWork = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/start_work`, payload);
        return response.data;
    } catch (error) {
        console.error('Error starting issue work:', error);
        throw error;
    }
};

/** Pause the work-tracking clock. @param {Object} payload - { issue_report_id, reason, paused_by } */
export const pauseIssueWork = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/pause_work`, payload);
        return response.data;
    } catch (error) {
        console.error('Error pausing issue work:', error);
        throw error;
    }
};

/** Resume the work-tracking clock. @param {Object} payload - { issue_report_id, resumed_by } */
export const resumeIssueWork = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/resume_work`, payload);
        return response.data;
    } catch (error) {
        console.error('Error resuming issue work:', error);
        throw error;
    }
};

/** End work tracking for an issue. @param {Object} payload - { issue_report_id, ended_by } */
export const endIssueWork = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/end_work`, payload);
        return response.data;
    } catch (error) {
        console.error('Error ending issue work:', error);
        throw error;
    }
};

/** Full pause/resume history for one issue. @param {Object} payload - { issue_report_id } */
export const getIssueWorkHistory = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/support/get_work_history`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching issue work history:', error);
        throw error;
    }
};

/**
 * List vouchers
 * @param {Object} params - e.g. { userId, client_id }
 */
export const getVouchers = async (params) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/get_cashvochersdetails`, params || {});
        return response.data;
    } catch (error) {
        console.error('Error fetching vouchers:', error);
        throw error;
    }
};

export const updateVoucher = async (payload) => {
    try {
        const isFormData = payload instanceof FormData;
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/update_vocherentry`, payload, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {});
        return response.data;
    } catch (error) {
        console.error('Error updating voucher:', error);
        throw error;
    }
};

/**
 * Create new expense
 * @param {Object} payload - { expanses_name, branch_id }
 */
export const createExpense = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/add_expanses`, payload);
        return response.data;
    } catch (error) {
        console.error('Error creating expense:', error);
        throw error;
    }
};

/**
 * Get expenses list
 */
export const getExpenses = async () => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/get_expansesdetails`, {});
        return response.data;
    } catch (error) {
        console.error('Error fetching expenses:', error);
        throw error;
    }
};

/**
 * Update expense
 * @param {Object} payload - { expa_id, expanses_name, branch_id }
 */
export const updateExpense = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/update_expanses`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating expense:', error);
        throw error;
    }
};

/**
 * Clear data by date range and branch
 * @param {Object} payload - { from_date, to_date, branch_id, cleared_by }
 */
export const clearDataByDateRange = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/counter_delete_records`, payload);
        return response.data;
    } catch (error) {
        console.error('Error clearing data:', error);
        throw error;
    }
};

/**
 * Fetch available dates for data operations (delete / process)
 * @param {Object} params - e.g. { branch_id, client_id }
 * @returns {Promise<Object>} - { dates: [] }
 */
export const getAvailableDates = async (params) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/counter/get_available_dates`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching available dates:', error);
        throw error;
    }
};

/**
 * Delete counter data for a specific date / branch
 * @param {Object} params - e.g. { date, branch_id }
 * @returns {Promise<Object>} - { status, message }
 */
export const deleteCounterData = async (params) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/counter/delete_data`, { params });
        return response.data;
    } catch (error) {
        console.error('Error deleting counter data:', error);
        throw error;
    }
};

/**
 * Process / sync counter data update
 * @param {Object} payload - e.g. { branch_id, date, processed_by }
 * @returns {Promise<Object>} - { status, message }
 */
export const processCounterUpdate = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/counter/process_update`, payload);
        return response.data;
    } catch (error) {
        console.error('Error processing counter update:', error);
        throw error;
    }
};

/**
 * Fetch location details (clients, branches, locations).
 * @param {Object} payload - { rolename, clientname }
 * @returns {Promise<Object>} - { clients: [], branchs: [], company: [] }
 */
export const getLocationDetails = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/get_locationdetails`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching location details:', error);
        throw error;
    }
};

/**
 * Save (add / edit) a location.
 * @param {Object} payload - Location object (location_id null for new)
 * @returns {Promise<Object>} - { status: boolean, message: string }
 */
export const saveLocation = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/saveLocation`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving location:', error);
        throw error;
    }
};

/**
 * Delete a location by ID.
 * @param {Object} payload - { location_id }
 * @returns {Promise<Object>} - { status: boolean, message: string }
 */
export const deleteLocation = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/deleteLocation`, payload);
        return response.data;
    } catch (error) {
        console.error('Error deleting location:', error);
        throw error;
    }
};

/**
 * Get category masters details.
 * @param {Object} payload
 * @param {string} payload.clientname - Client ID
 * @param {string} payload.rolename - Role name (e.g. 'CEO', 'admin', 'manager')
 * @returns {Promise<Object>} - { clients: [], category_masters: [] }
 */
export const getcategorymasters = async (payload) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/get_categorymasters`, { params: payload });
        return response.data;
    } catch (error) {
        console.error('Error fetching category masters:', error);
        throw error;
    }
};

/**
 * Save (add / edit) category masters.
 * @param {Object} payload
 * @param {Array}  payload.categories  - Array of category objects
 * @param {string|null} payload.categories[].category_master_id - null for add, id for edit
 * @param {string} payload.categories[].category_name
 * @param {string} payload.categories[].client_id
 * @param {string} payload.categories[].status        - 'Active' | 'Inactive'
 * @param {string|null} payload.categories[].effective_from - YYYY-MM-DD
 * @param {string} payload.updated_by - logged-in user id
 * @returns {Promise<Object>} - { status: 'success'|'error', message: string }
 */
export const savecategorymasters = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/save_categorymasters`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving category masters:', error);
        throw error;
    }
};

/**
 * Fetch company details (clients, branches, companies, locations, platforms).
 * @param {Object} payload - { rolename, clientname }
 * @returns {Promise<Object>} - { clients: [], branchs: [], company: [], locations: [], platform: [] }
 */
export const getCompanyDetails = async (payload) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/masters/get_compantdetails`, { params: payload });
        return response.data;
    } catch (error) {
        console.error('Error fetching company details:', error);
        throw error;
    }
};

/**
 * Save (add / edit) a company.
 * @param {Object} payload - Company object (company_id null for new)
 * @returns {Promise<Object>} - { status: boolean, message: string }
 */
export const saveCompany = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/saveCompany`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving company:', error);
        throw error;
    }
};

export const getAssignedProductsData = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/get_itemsassigneddetails`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching assigned products data:', error);
        throw error;
    }
};

export const saveAssignedProduct = async (payload) => {
    try {
        const endpoint = payload.spl_id ? `${getSalesReactBaseUrl()}/api/masters/updateAssignedProduct` : `${getSalesReactBaseUrl()}/api/masters/addAssignedProduct`;
        const response = await axios.post(endpoint, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving assigned product:', error);
        throw error;
    }
};

/**
 * Delete a company by ID.
 * @param {Object} payload - { company_id }
 * @returns {Promise<Object>} - { status: boolean, message: string }
 */
export const deleteCompany = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/deleteCompany`, payload);
        return response.data;
    } catch (error) {
        console.error('Error deleting company:', error);
        throw error;
    }
};

/**
 * Fetch feedback records, optionally filtered by date range.
 * @param {Object} payload - { client_id, branch_id, date_from, date_to } (all optional, dates 'YYYY-MM-DD')
 * @returns {Promise<Object>} - { status: boolean, feedback: Array }
 */
export const getFeedbackList = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/get_feedbackdetails`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching feedback details:', error);
        throw error;
    }
};

/**
 * Save (add / edit) a feedback record.
 * @param {Object} payload - Feedback object (feedback_id null for new)
 * @returns {Promise<Object>} - { status: boolean, message: string }
 */
export const saveFeedback = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/saveFeedback`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving feedback:', error);
        throw error;
    }
};

export const deleteFeedback = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/deleteFeedback`, payload);
        return response.data;
    } catch (error) {
        console.error('Error deleting feedback:', error);
        throw error;
    }
};

export const saveModuleGroup = async (payload) => {
    try {
        const endpoint = payload.module_group_id ? `${SALES_MASTER_ADMIN_BASE_URL}/api/server_masters/updateModuleGroup/${payload.module_group_id}` : `${SALES_MASTER_ADMIN_BASE_URL}/api/server_masters/addModuleGroup`;
        const response = await axios.post(endpoint, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving module group:', error);
        throw error;
    }
};

/**
 * Get product category details.
 * @param {Object} payload
 * @param {string} payload.rolename   - Role name (e.g. 'CEO', 'admin', 'manager')
 * @param {string} payload.clientname - Client ID
 * @param {string} [payload.branch_id] - Branch ID (optional filter)
 * @returns {Promise<Object>} - { clients: [], branches: [], product_category: [] }
 */
export const getproductcategory = async (payload) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/get_productcategory`, { params: payload });
        return response.data;
    } catch (error) {
        console.error('Error fetching product categories:', error);
        throw error;
    }
};

/**
 * Update product category record.
 * @param {Object} payload
 * @param {number} payload.prcat_cat_id
 * @param {string} payload.prcat_cat_name
 * @param {string} payload.prcat_ref_name
 * @param {number} payload.prcat_discount
 * @param {string} payload.prcat_tokengroup
 * @param {string} payload.prcat_status  - 'Active' | 'Inactive'
 * @param {string|null} payload.effective_from  - YYYY-MM-DD
 * @param {string} payload.updated_by
 * @returns {Promise<Object>} - { status: 'success'|'error', message: string }
 */
export const saveProductCategory = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/masters/save_productcategory`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving product category:', error);
        throw error;
    }
};

export const updateProductCategory = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_ADMIN_BASE_URL}/api/server_masters/update_productcategory`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating product category:', error);
        throw error;
    }
};

/**
 * Bulk update product category status.
 * @param {Object} payload
 * @param {number[]} payload.ids   - Array of prcat_cat_id values
 * @param {string}   payload.status - 'Active' | 'Inactive'
 * @returns {Promise<Object>} - { status: 'success'|'error', message?: string }
 */
export const updateProductCategoryStatus = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_ADMIN_BASE_URL}/api/server_masters/bulk_status_update/productcategory`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating product category status:', error);
        throw error;
    }
};

export const bulkSettingAccess = async (table, payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_status_update/${table}`, payload);
        return response.data;
    } catch (error) {
        console.error(`Error in bulk setting access (${table}):`, error);
        throw error;
    }
};

export const bulkUpdateProductCategoryStatus = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulkStatusUpdateProductCategory`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk updating product category status:', error);
        throw error;
    }
};

// ─── Notifications (client alerts) ─────────────────────────────────────────────
// These are server-side endpoints: the notifications tables live only in the master
// database, so they are never served by a client deployment. Each helper takes an
// optional trailing `baseUrl` — same convention as the gsbilling helpers below — so
// the endpoint-tester pages can aim a call at a specific deployment. Omitting it
// keeps the normal behaviour of calling the backend this UI is served from.
const notificationBase = (baseUrl) => (baseUrl ? normalizeBaseUrl(baseUrl) : SALES_MASTER_BASE_URL);

/**
 * List client alert notifications.
 * @param {Object} [params] - Optional filters
 * @param {string|number} [params.client_id] - Scope to one client (non-GSPOS roles must pass their own)
 * @param {string|number} [params.branch_id] - Scope to one branch (also returns client-wide alerts)
 * @param {string} [params.status] - 'Active' | 'Inactive'
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Array>} - Array of notification rows
 */
export const getNotifications = async (params = {}, baseUrl) => {
    try {
        const response = await axios.get(`${notificationBase(baseUrl)}/api/server_masters/getNotifications`, { params });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching notifications:', error);
        throw error;
    }
};

/**
 * Alerts a client should be shown right now — Active and not past their expiry.
 * @param {string|number} clientId
 * @param {string|number} [branchId]
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Array>}
 */
export const getActiveNotifications = async (clientId, branchId = '', baseUrl) => {
    try {
        const response = await axios.get(`${notificationBase(baseUrl)}/api/server_masters/getActiveNotifications`, {
            params: { client_id: clientId, branch_id: branchId }
        });
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching active notifications:', error);
        throw error;
    }
};

/**
 * Notifications a branch receives, addressed by branch name the way the gsbilling /
 * live_data endpoints are — e.g. .../getNotifications/AST5. The branch resolves to its
 * own client on the server, so the result is its own alerts plus the client-wide ones.
 * An unknown branch answers HTTP 404.
 * @param {string} segment - Branch name
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Array>}
 */
export const getNotificationsByBranch = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${notificationBase(baseUrl)}/api/server_masters/getNotifications/${encodeURIComponent(segment)}`);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching notifications by branch:', error);
        throw error;
    }
};

/**
 * Active, unexpired alerts for one branch, addressed by branch name.
 * @param {string} segment - Branch name
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Array>}
 */
export const getActiveNotificationsByBranch = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${notificationBase(baseUrl)}/api/server_masters/getActiveNotifications/${encodeURIComponent(segment)}`);
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Error fetching active notifications by branch:', error);
        throw error;
    }
};

/**
 * Create a notification, or fan the same one out to several targets at once.
 *
 * Targeting (one row is created per target):
 *   client_id / branch_id      — a single alert
 *   client_ids: [..]           — the same alert for each client, all branches
 *   client_ids: [one], branch_ids: [..] — several branches of that one client
 *
 * Authoring is GSPOS-only and enforced server-side, so `acting_user_id` is required.
 * @param {Object} payload
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Object>} - { status, message, notification_id, created_ids }
 */
export const addNotification = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${notificationBase(baseUrl)}/api/server_masters/addNotification`, payload, {
            headers: { 'Content-Type': 'application/json' }
        });
        return response.data;
    } catch (error) {
        console.error('Error adding notification:', error);
        throw error;
    }
};

/**
 * Update an existing notification.
 * @param {string|number} notificationId
 * @param {Object} payload
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Object>} - { status, message }
 */
export const updateNotification = async (notificationId, payload, baseUrl) => {
    try {
        const response = await axios.post(`${notificationBase(baseUrl)}/api/server_masters/updateNotification/${notificationId}`, payload, {
            headers: { 'Content-Type': 'application/json' }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating notification:', error);
        throw error;
    }
};

/**
 * Create or update a notification — the form's single save call. Passing
 * notification_id switches it to an update (always a single target).
 * @param {Object} payload - Must include acting_user_id; see addNotification for targeting
 * @returns {Promise<Object>} - { status, message, notification_id?, created_ids? }
 */
export const saveNotification = async (payload) => {
    const { notification_id: id, ...body } = payload;
    return id ? updateNotification(id, body) : addNotification(body);
};

/**
 * Who has and hasn't read one notification — powers the detail panel under the list.
 * @param {string|number} notificationId
 * @param {string} [baseUrl] - Target a specific deployment
 * @returns {Promise<Object>} - { read: [], unread: [], read_count, recipient_count }
 */
export const getNotificationReadStatus = async (notificationId, baseUrl) => {
    try {
        const response = await axios.get(`${notificationBase(baseUrl)}/api/server_masters/getNotificationReadStatus/${notificationId}`);
        return response.data || { read: [], unread: [], read_count: 0, recipient_count: 0 };
    } catch (error) {
        console.error('Error fetching notification read status:', error);
        throw error;
    }
};

/**
 * Notification feed for the topbar bell. Visibility (GSPOS = all clients, CEO = own
 * client, other roles = own branch + client-wide) is decided server-side from the user id.
 * `unread_count` is always the full unread total, even when unreadOnly trims the items.
 * @param {string|number} userId
 * @param {Object} [options]
 * @param {number} [options.limit=50]
 * @param {boolean} [options.unreadOnly=false] - Return only notifications this user has not read
 * @param {string} [options.baseUrl] - Target a specific deployment
 * @returns {Promise<Object>} - { status, role, unread_count, items: [] }
 */
export const getUserNotifications = async (userId, { limit = 50, unreadOnly = false, baseUrl } = {}) => {
    try {
        const response = await axios.get(`${notificationBase(baseUrl)}/api/server_masters/getUserNotifications/${userId}`, {
            params: { limit, unread_only: unreadOnly }
        });
        return response.data || { unread_count: 0, items: [] };
    } catch (error) {
        console.error('Error fetching user notifications:', error);
        throw error;
    }
};

/**
 * Mark one notification read for a user, optionally with the user's reply.
 * Omitting remarks leaves any existing reply untouched.
 * @param {string|number} userId
 * @param {string|number} notificationId
 * @param {string} [remarks] - The user's reply
 * @param {string} [baseUrl] - Target a specific deployment
 */
export const markNotificationRead = async (userId, notificationId, remarks = '', baseUrl) => {
    try {
        const response = await axios.post(`${notificationBase(baseUrl)}/api/server_masters/markNotificationRead`, {
            user_id: userId,
            notification_id: notificationId,
            remarks
        });
        return response.data;
    } catch (error) {
        console.error('Error marking notification read:', error);
        throw error;
    }
};

/**
 * Mark every notification currently visible to the user as read.
 * @param {string|number} userId
 * @param {string} [baseUrl] - Target a specific deployment
 */
export const markAllNotificationsRead = async (userId, baseUrl) => {
    try {
        const response = await axios.post(`${notificationBase(baseUrl)}/api/server_masters/markAllNotificationsRead`, { user_id: userId });
        return response.data;
    } catch (error) {
        console.error('Error marking all notifications read:', error);
        throw error;
    }
};

// ─── Mobile Billing App (apk_backup) ──────────────────────────────────────────
// The APK register lives only in the master DB, so these always go to the master
// deployment — same reasoning as the notification endpoints above.

/**
 * List APK entries.
 * @param {Object} [params] - { client_id, branch_id, status }
 * @returns {Promise<Array>} - Array of apk_backup rows (client_name/branch_name joined,
 *   plus direct_download_url and download_url)
 */
export const getApkBackups = async (params = {}) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/mobilebillingapp/list`, { params });
        return response.data?.data || [];
    } catch (error) {
        console.error('Error fetching APK backups:', error);
        throw error;
    }
};

/**
 * Create or update an APK entry — the form's single save call. Passing apkid
 * switches it to an update.
 * @param {Object} payload - { apkid?, client_id, branch_id, app_version,
 *   play_storelink, googledrive_link, status, acting_user }
 * @returns {Promise<Object>} - { status, message, apkid }
 */
export const saveApkBackup = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/mobilebillingapp/save`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving APK backup:', error);
        throw error;
    }
};

/**
 * Email one entry's install links to a requester.
 * @param {Object} payload - { apkid, email, shared_by, shared_by_email, message }
 * @returns {Promise<Object>} - { status, message }
 */
export const shareApkBackup = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/mobilebillingapp/share`, payload);
        return response.data;
    } catch (error) {
        console.error('Error sharing APK backup:', error);
        throw error;
    }
};

/**
 * URL that redirects straight to the APK file — safe to use as an <a href> or
 * window.open target.
 * @param {string|number} apkid
 */
export const getApkDownloadUrl = (apkid) => `${SALES_MASTER_BASE_URL}/api/mobilebillingapp/download/${apkid}`;

/**
 * Get module groups details.
 *
 */
export const getModuleGroups = async () => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/getModuleGroups`);
        return response.data;
    } catch (error) {
        console.error('Error fetching module groups:', error);
        throw error;
    }
};

export const getModuleMenus = async () => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/getModuleMenus`);
        return response.data;
    } catch (error) {
        console.error('Error fetching module menus:', error);
        throw error;
    }
};

/**
 * GET /api/server_masters/getusermodulesassigndetails
 * Returns:
 *   clients – [{ client_id, client_name }]
 *   branchs – [{ branch_id, branch_name }]           (no client_id — derive via modules)
 *   users   – [{ userId, username }]                  (no client_id — derive via modules)
 *   modules – [{ user_id, client_id, branch_id, client_name, branch_name, username,
 *                module_group_name, module_menu_id, module_name, link, icon,
 *                module_user_id, status }]
 */
export const getUserModules = async () => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/getusermodulesassigndetails`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user modules:', error);
        throw error;
    }
};

// ─── ODC (Outdoor Catering) endpoints ────────────────────────────────────────

const ODC_FILTER_ENDPOINTS = {
    pending: 'filtter_odcdefaultlist',
    dispatched: 'filter_dispatched_list',
    setteled: 'filter_settled_list',
    cancelled: 'filter_cancelled_list',
    dispatchedonly: 'filter_only_dispached'
};

export const getOdcHeaderData = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/get_odc_headernew`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC header data:', error);
        throw error;
    }
};

export const getOdcOrderById = async (id) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/outdoorcatering/getOrderById/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC order by ID:', error);
        throw error;
    }
};

export const updateOdcOrderCart = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/updateOrderCart`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating ODC order cart:', error);
        throw error;
    }
};

export const insertNewOdcOrder = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/insertNewodcOrder`, payload);
        return response.data;
    } catch (error) {
        console.error('Error inserting new ODC order:', error);
        throw error;
    }
};

export const getOdcDefaultData = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/get_defaultodcdata`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC default data:', error);
        throw error;
    }
};

export const filterOdcOrders = async (status, payload, signal = null) => {
    try {
        const endpoint = ODC_FILTER_ENDPOINTS[status] || 'filtter_odcdefaultlist';
        const config = signal ? { signal } : {};
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/${endpoint}`, payload, config);
        return response.data;
    } catch (error) {
        console.error(`Error filtering ODC orders (${status}):`, error);
        throw error;
    }
};

export const bulkUpdateOdcStatus = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/bulk_update_status`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk updating ODC status:', error);
        throw error;
    }
};

export const cancelOdcOrder = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/cancel_order`, payload);
        return response.data;
    } catch (error) {
        console.error('Error cancelling ODC order:', error);
        throw error;
    }
};

export const getOdcCustomers = async (branchId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/outdoorcatering/getCustomers`, { params: { branch_id: branchId } });
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC customers:', error);
        throw error;
    }
};

export const getOdcPaymentModes = async (branchId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/outdoorcatering/getPaymentModes`, { params: { branch_id: branchId } });
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC payment modes:', error);
        throw error;
    }
};

export const settleOdcOrder = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/settleOrder`, payload);
        return response.data;
    } catch (error) {
        console.error('Error settling ODC order:', error);
        throw error;
    }
};

export const dispatchOdcOrder = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/dispatchOrder`, payload);
        return response.data;
    } catch (error) {
        console.error('Error dispatching ODC order:', error);
        throw error;
    }
};

// Legacy header endpoint (get_odc_header — older version of get_odc_headernew)
export const getOdcHeaderLegacy = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/get_odc_header`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC header (legacy):', error);
        throw error;
    }
};

// ODC-specific dispatch status update (separate from institute updateDispatchStatus)
export const updateOdcDispatchStatus = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/updateDispatchStatus`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating ODC dispatch status:', error);
        throw error;
    }
};

// Alternative dispatch endpoint (dispatchOrder111)
export const dispatchOdcOrderAlt = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/dispatchOrder111`, payload);
        return response.data;
    } catch (error) {
        console.error('Error dispatching ODC order (alt):', error);
        throw error;
    }
};

// Fetch ODC products for order entry
export const getOdcProducts = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/getodcproducts`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC products:', error);
        throw error;
    }
};

// Fetch institute bills list under ODC context
export const getOdcInstituteListBills = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/get_institutelistbills`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ODC institute list bills:', error);
        throw error;
    }
};

// Filter institute bills under ODC context
export const filterOdcInstituteBills = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/outdoorcatering/filtter_institutebills`, payload);
        return response.data;
    } catch (error) {
        console.error('Error filtering ODC institute bills:', error);
        throw error;
    }
};

// ─── Institute ────────────────────────────────────────────────────────────────

export const cancelInstituteOrder = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/cancel_order`, payload);
        return response.data;
    } catch (error) {
        console.error('Error cancelling institute order:', error);
        throw error;
    }
};

// Fetch institute bills list (summary view)
export const getInstituteListBills = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/get_institutelistbills`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching institute list bills:', error);
        throw error;
    }
};

// Filter institute bills by date/branch/status
export const filterInstituteBills = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/institute/filtter_institutebills`, payload);
        return response.data;
    } catch (error) {
        console.error('Error filtering institute bills:', error);
        throw error;
    }
};

// ─── Token Display ───────────────────────────────────────────────────────────

export const tokenDisplayLogin = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/token_display/login_authenticate`, payload);
        return response.data;
    } catch (error) {
        console.error('Error authenticating token display login:', error);
        throw error;
    }
};

export const getTokens = async (params) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/token_display/get_tokens`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching tokens:', error);
        throw error;
    }
};

export const getParcelTokens = async (params) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/token_display/parcel_tokens`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching parcel tokens:', error);
        throw error;
    }
};

export const callbackTokens = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/token_display/callback_tokens`, payload);
        return response.data;
    } catch (error) {
        console.error('Error calling back tokens:', error);
        throw error;
    }
};

export const systemDisplayApi = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/token_display/system_display_api`, payload);
        return response.data;
    } catch (error) {
        console.error('Error calling system display API:', error);
        throw error;
    }
};

export const systemDisplayApi2 = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/token_display/system_display_api2`, payload);
        return response.data;
    } catch (error) {
        console.error('Error calling system display API 2:', error);
        throw error;
    }
};

export const callbackParcelTokens = async (payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/token_display/callback_parceltokens`, payload);
        return response.data;
    } catch (error) {
        console.error('Error calling back parcel tokens:', error);
        throw error;
    }
};

export const callSocketSystem = async (params) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/token_display/callsocketsystem`, { params });
        return response.data;
    } catch (error) {
        console.error('Error calling socket system:', error);
        throw error;
    }
};

export const callParcelAuth = async (params) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/token_display/callparcelAuth`, { params });
        return response.data;
    } catch (error) {
        console.error('Error calling parcel auth:', error);
        throw error;
    }
};

// ─── Server Masters — generic CRUD ───────────────────────────────────────────

export const getServerMastersList = async (table) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/list/${table}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching server masters list (${table}):`, error);
        throw error;
    }
};

export const saveServerMaster = async (table, payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/save/${table}`, payload);
        return response.data;
    } catch (error) {
        console.error(`Error saving server master (${table}):`, error);
        throw error;
    }
};

export const sendSmsTest = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/send_sms_test`, payload);
        return response.data;
    } catch (error) {
        console.error('Error sending SMS test:', error);
        throw error;
    }
};

export const provisionApplicationSettings = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/provision_application_settings`, payload);
        return response.data;
    } catch (error) {
        console.error('Error provisioning application settings:', error);
        throw error;
    }
};

// ─── Product endpoints ────────────────────────────────────────────────────────

export const getProductList = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/get_productlist`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching product list:', error);
        throw error;
    }
};

export const saveProduct = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/save_product`, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving product:', error);
        throw error;
    }
};

export const updateProduct = async (prId, payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/update_product/${prId}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error updating product:', error);
        throw error;
    }
};

export const bulkStatusUpdateProduct = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_status_update/product`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk updating product status:', error);
        throw error;
    }
};

export const bulkProductStatusChange = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_product_status_change`, payload);
        return response.data;
    } catch (error) {
        console.error('Error in bulk product status change:', error);
        throw error;
    }
};

export const bulkPromoteProducts = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_promote`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk promoting products:', error);
        throw error;
    }
};

export const bulkImportProducts = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_import_products`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk importing products:', error);
        throw error;
    }
};

export const bulkTokenUpdate = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_token`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk updating token:', error);
        throw error;
    }
};

export const bulkDiscountUpdate = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_discount`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk updating discount:', error);
        throw error;
    }
};

export const bulkPriceChange = async (payload) => {
    try {
        const response = await axios.post(`${SALES_MASTER_BASE_URL}/api/server_masters/bulk_price_change`, payload);
        return response.data;
    } catch (error) {
        console.error('Error bulk changing price:', error);
        throw error;
    }
};

// ─── Live Data Diagnostics (Counter-Backend live sync / SMS / day-end testing) ─
// Uses the same dynamic base URL resolution as the rest of the app: dev proxy on
// localhost, and the configured server URL in production.
export const getLiveDataClients = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/server_masters/list/client_creation`);
        return response.data || [];
    } catch (error) {
        console.error('Error fetching live data clients:', error);
        throw error;
    }
};

export const getLiveDataBranches = async (clientId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/server_masters/branches`, { params: { client_id: clientId } });
        return response.data || [];
    } catch (error) {
        console.error('Error fetching live data branches:', error);
        throw error;
    }
};

export const resetLiveDataMasters = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/server_masters/master_table_reset/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error resetting live data masters:', error);
        throw error;
    }
};

export const generateCounterPayloadLive = async (segment) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/live_data/generate_counter_payload/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error generating counter payload (live):', error);
        throw error;
    }
};

export const getConfirationBillsDetails = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Confiration_bills_details/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching configuration bills details:', error);
        throw error;
    }
};

export const getSalesTrackingRecords = async (segment) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/live_data/sales_tracking_records/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching sales tracking records:', error);
        throw error;
    }
};

export const pushPosDataSync = async (segment, payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Pos_data_synching/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error pushing POS data sync:', error);
        throw error;
    }
};

export const getSmsSentDetails = async (segment) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/live_data/sms_sentdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching SMS sent details:', error);
        throw error;
    }
};

export const getDaycloseDetails = async (segment) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/live_data/dayclose_details/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching day close details:', error);
        throw error;
    }
};

export const getClientCountUpdation = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/get_client_countupdation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client count updation:', error);
        throw error;
    }
};

export const getApplicationSettingLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_application_setting/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching application setting (live):', error);
        throw error;
    }
};

export const getMastersCreationLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_masters_creation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching masters creation (live):', error);
        throw error;
    }
};

export const getOutletDetailsLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_outlet_deatils/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching outlet details (live):', error);
        throw error;
    }
};

export const getProductCategoryLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_product_category/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product category (live):', error);
        throw error;
    }
};

export const getProductLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_product/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product (live):', error);
        throw error;
    }
};

export const getUsersLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_users/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching users (live):', error);
        throw error;
    }
};

export const getNotificationsLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_notifications/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching notifications (live):', error);
        throw error;
    }
};

export const callbackNotificationsLive = async (segment, payload) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/callback_notifications/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging notifications (live):', error);
        throw error;
    }
};

export const getCreditCustomerLive = async (segment) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/live_data/Get_credit_customer/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit customer (live):', error);
        throw error;
    }
};

export const getDataSynchingDetails = async (segment) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/client_data/Data_sychingdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching data synching details:', error);
        throw error;
    }
};

export const getBranchConfigurationDetailsLive = async (segment) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/live_data/branch_configrationdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching branch configuration details (live):', error);
        throw error;
    }
};

export const getCompanyDetailsAdmin = async (payload) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/get_companydetails`, { params: payload });
        return response.data;
    } catch (error) {
        console.error('Error fetching company details (admin):', error);
        throw error;
    }
};

export const getLocationDetailsAdmin = async (payload) => {
    try {
        const response = await axios.get(`${SALES_MASTER_BASE_URL}/api/server_masters/get_locationdetails`, { params: payload });
        return response.data;
    } catch (error) {
        console.error('Error fetching location details (admin):', error);
        throw error;
    }
};

// ─── GS Billing (gsbilling) Endpoints — Auth & Bill Sync, Master Data Sync ─────
// Each helper takes an optional `baseUrl` so a caller can target the server
// deployment (client_creation.server_url) or the client's own deployment
// (client_creation.base_url) instead of the backend this UI is served from.
// Omitting it keeps the previous behaviour — the current dynamic base URL.
const gsbillingBase = (baseUrl) => (baseUrl ? normalizeBaseUrl(baseUrl) : getSalesReactBaseUrl());

/**
 * Second-opinion probe used only after a request died without an HTTP response.
 *
 * A `no-cors` fetch is exempt from the CORS response check: the browser still performs
 * the full round trip but hands back an opaque response instead of blocking. So if this
 * resolves, the host answered and only the CORS policy hid the reply from us; if it
 * rejects, the failure is lower down — DNS, TLS or a refused connection.
 *
 * It proves the host is contactable, not that the endpoint works: an opaque response
 * carries no status, so a 404 or a 500 resolves here just like a 200.
 */
const hostAnswersOpaquely = async (url) => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 10000);
    try {
        await fetch(url, { mode: 'no-cors', cache: 'no-store', signal: abort.signal });
        return true;
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
    }
};

/**
 * Reachability probe for one gsbilling deployment, mirroring the backend's own check:
 * a 200 + JSON body from Get_application_setting means routing, PHP and the master
 * database are all alive on that host.
 *
 * When no HTTP response comes back, axios reports a bare "Network Error" whether the
 * host is dead or the browser threw the reply away for failing CORS. Those need very
 * different fixes, so re-probe with `no-cors` and report which one it was via `blocked`.
 *
 * @param {string} baseUrl - Host to probe, e.g. https://gsonlinesolutions.com/Counter-Backend
 * @param {string} segment - Branch name used for the probe request
 * @returns {Promise<{ok: boolean, statusCode: number, duration: number, blocked?: boolean, timedOut?: boolean, error?: string}>}
 */
export const probeGsbillingHost = async (baseUrl, segment) => {
    const url = `${gsbillingBase(baseUrl)}/api/gsbilling/Get_application_setting/${encodeURIComponent(segment)}`;
    const startedAt = Date.now();
    try {
        const response = await axios.get(url, { timeout: 10000 });
        return { ok: response.status === 200 && response.data !== null && typeof response.data === 'object', statusCode: response.status, duration: Date.now() - startedAt };
    } catch (error) {
        const duration = Date.now() - startedAt;

        // The host replied, just not with something we accept — that status is the whole story.
        if (error.response?.status) return { ok: false, statusCode: error.response.status, duration, error: error.message };

        // Nothing came back in time. A CORS block fails immediately, never on the timeout,
        // so there is no point asking the opaque probe — the host is simply not answering.
        if (error.code === 'ECONNABORTED') return { ok: false, statusCode: 0, duration, timedOut: true, error: error.message };

        const blocked = await hostAnswersOpaquely(url);
        return {
            ok: false,
            statusCode: 0,
            duration,
            blocked,
            error: blocked ? `${error.message} — the host answered but the browser blocked the response (CORS). Add this page's origin to that deployment's Config/Cors.php allowedOrigins.` : error.message
        };
    }
};

export const gsbillingLoginAuthenticate = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/login_authenticate`, payload);
        return response.data;
    } catch (error) {
        console.error('Error authenticating (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingBillDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/get_billdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching bill details (gsbilling):', error);
        throw error;
    }
};

export const syncGsbillingCounterData = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/counterdata_synching/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error synching counter data (gsbilling):', error);
        throw error;
    }
};

export const sendGsbillingSms = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/send_sms/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending SMS (gsbilling):', error);
        throw error;
    }
};

export const sendGsbillingSalesTrackingRecords = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/sales_tracking_records/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending sales tracking records (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingClientCountUpdation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/get_client_countupdation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client count updation (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingApplicationSetting = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_application_setting/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching application setting (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingApplicationSetting = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_application_setting/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging application setting (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingMastersCreation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_masters_creation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching masters creation (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingMastersCreation = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_masters_creation/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging masters creation (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingCreditCustomer = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_credit_customer/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit customer (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingCreditCustomer = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_credit_customer/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging credit customer (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingOutletDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_outlet_deatils/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching outlet details (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingOutletDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_outlet_deatils/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging outlet details (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingProductCategory = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_product_category/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product category (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingProductCategory = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_product_category/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product category (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingProduct = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_product/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingProduct = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_product/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingUsers = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_users/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching users (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingNotifications = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_notifications/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching notifications (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingNotifications = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_notifications/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging notifications (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingUsers = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_users/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging users (gsbilling):', error);
        throw error;
    }
};

export const getGsbillingPrinterDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${gsbillingBase(baseUrl)}/api/gsbilling/Get_printer_details/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching printer details (gsbilling):', error);
        throw error;
    }
};

export const callbackGsbillingPrinterDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${gsbillingBase(baseUrl)}/api/gsbilling/callback_printer_details/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging printer details (gsbilling):', error);
        throw error;
    }
};

// ─── Vikraya App (Client\Vikraya) Endpoints ────────────────────────────────────
// All 29 routes live on Client\Vikraya, i.e. the client's own deployment
// (client_creation.base_url) — there is no separate server-side split like
// GS Billing has. `baseUrl` is still optional and falls back to the current
// dynamic base URL, kept only so a caller can target a specific client explicitly.
const vikrayaBase = (baseUrl) => (baseUrl ? normalizeBaseUrl(baseUrl) : getSalesReactBaseUrl());

export const vikrayaLoginAuthenticate = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/login_authenticate`, payload);
        return response.data;
    } catch (error) {
        console.error('Error authenticating (vikraya):', error);
        throw error;
    }
};

export const getVikrayaBillDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_billdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching bill details (vikraya):', error);
        throw error;
    }
};

export const syncVikrayaCounterData = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/counterdata_synching/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error synching counter data (vikraya):', error);
        throw error;
    }
};

export const sendVikrayaSms = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/send_sms/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending SMS (vikraya):', error);
        throw error;
    }
};

export const sendVikrayaSalesTrackingRecords = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/sales_tracking_records/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending sales tracking records (vikraya):', error);
        throw error;
    }
};

export const getVikrayaClientCountUpdation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_client_countupdation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client count updation (vikraya):', error);
        throw error;
    }
};

export const getVikrayaApplicationSetting = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_application_setting/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching application setting (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaApplicationSetting = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_application_setting/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging application setting (vikraya):', error);
        throw error;
    }
};

export const getVikrayaMastersCreation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_masters_creation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching masters creation (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaMastersCreation = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_masters_creation/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging masters creation (vikraya):', error);
        throw error;
    }
};

export const getVikrayaCreditCustomer = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_credit_customer/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit customer (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaCreditCustomer = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_credit_customer/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging credit customer (vikraya):', error);
        throw error;
    }
};

export const getVikrayaOutletDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_outlet_deatils/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching outlet details (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaOutletDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_outlet_deatils/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging outlet details (vikraya):', error);
        throw error;
    }
};

export const getVikrayaProductCategory = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_product_category/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product category (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaProductCategory = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_product_category/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product category (vikraya):', error);
        throw error;
    }
};

export const getVikrayaProduct = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_product/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaProduct = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_product/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product (vikraya):', error);
        throw error;
    }
};

export const getVikrayaUsers = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_users/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching users (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaUsers = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_users/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging users (vikraya):', error);
        throw error;
    }
};

export const getVikrayaPrinterDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_printer_details/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching printer details (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaPrinterDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_printer_details/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging printer details (vikraya):', error);
        throw error;
    }
};

export const getVikrayaNotifications = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/Get_notifications/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching notifications (vikraya):', error);
        throw error;
    }
};

export const callbackVikrayaNotifications = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/callback_notifications/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging notifications (vikraya):', error);
        throw error;
    }
};

export const getVikrayaCreditReport = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_credit_report/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit report (vikraya):', error);
        throw error;
    }
};

export const getVikrayaLastBillDetails = async (segment, custId, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_lastbill_details/${segment}/${custId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching last bill details (vikraya):', error);
        throw error;
    }
};

export const getVikrayaItemsReport = async (segment, userId, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_items_report/${segment}/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching items report (vikraya):', error);
        throw error;
    }
};

export const getVikrayaSpecialProductCust = async (segment, custId, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/special_product_cust/${segment}/${custId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching special product customer pricing (vikraya):', error);
        throw error;
    }
};

export const getVikrayaCustomerData = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_customer_data/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching customer data (vikraya):', error);
        throw error;
    }
};

export const uploadVikrayaImage = async (formData, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/upload_image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data;
    } catch (error) {
        console.error('Error uploading image (vikraya):', error);
        throw error;
    }
};

export const getVikrayaRefInformation = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_ref_inforamtion`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ref information (vikraya):', error);
        throw error;
    }
};

export const getVikrayaPaymentCallback = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${vikrayaBase(baseUrl)}/api/vikraya_app/get_payment_callback`, payload);
        return response.data;
    } catch (error) {
        console.error('Error posting payment callback (vikraya):', error);
        throw error;
    }
};

// ─── Rootsales App Endpoints ────────────────────────────────────────────────
// Same shape as Vikraya: Auth/billing-sync endpoints hit the client's own DB,
// master-data endpoints pull from the master DB — but all 32 routes are still
// served from a single host, the client's own deployment (client_creation.base_url).
const rootsalesBase = (baseUrl) => (baseUrl ? normalizeBaseUrl(baseUrl) : getSalesReactBaseUrl());

export const rootsalesLoginAuthenticate = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/login_authenticate`, payload);
        return response.data;
    } catch (error) {
        console.error('Error authenticating (rootsales):', error);
        throw error;
    }
};

export const getRootsalesBillDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/get_billdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching bill details (rootsales):', error);
        throw error;
    }
};

export const syncRootsalesVikrayaData = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/vikrayadata_synching/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error synching vikraya data (rootsales):', error);
        throw error;
    }
};

export const sendRootsalesSms = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/send_sms/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending SMS (rootsales):', error);
        throw error;
    }
};

export const sendRootsalesSalesTrackingRecords = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/sales_tracking_records/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending sales tracking records (rootsales):', error);
        throw error;
    }
};

export const getRootsalesClientCountUpdation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/get_client_countupdation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client count updation (rootsales):', error);
        throw error;
    }
};

export const getRootsalesApplicationSetting = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_application_setting/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching application setting (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesApplicationSetting = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_application_setting/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging application setting (rootsales):', error);
        throw error;
    }
};

export const getRootsalesMastersCreation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_masters_creation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching masters creation (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesMastersCreation = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_masters_creation/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging masters creation (rootsales):', error);
        throw error;
    }
};

export const getRootsalesCreditCustomer = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_credit_customer/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit customer (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesCreditCustomer = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_credit_customer/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging credit customer (rootsales):', error);
        throw error;
    }
};

export const getRootsalesOutletDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_outlet_deatils/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching outlet details (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesOutletDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_outlet_deatils/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging outlet details (rootsales):', error);
        throw error;
    }
};

export const getRootsalesProductCategory = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_product_category/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product category (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesProductCategory = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_product_category/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product category (rootsales):', error);
        throw error;
    }
};

export const getRootsalesProduct = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_product/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesProduct = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_product/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product (rootsales):', error);
        throw error;
    }
};

export const getRootsalesUsers = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_users/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching users (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesUsers = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_users/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging users (rootsales):', error);
        throw error;
    }
};

export const getRootsalesPrinterDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_printer_details/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching printer details (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesPrinterDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_printer_details/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging printer details (rootsales):', error);
        throw error;
    }
};

export const getRootsalesNotifications = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/Get_notifications/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching notifications (rootsales):', error);
        throw error;
    }
};

export const callbackRootsalesNotifications = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/callback_notifications/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging notifications (rootsales):', error);
        throw error;
    }
};

export const getRootsalesCreditReport = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/get_credit_report/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit report (rootsales):', error);
        throw error;
    }
};

export const getRootsalesLastBillDetails = async (segment, custId, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/get_lastbill_details/${segment}/${custId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching last bill details (rootsales):', error);
        throw error;
    }
};

export const getRootsalesItemsReport = async (segment, userId, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/get_items_report/${segment}/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching items report (rootsales):', error);
        throw error;
    }
};

export const getRootsalesSpecialProductCust = async (segment, custId, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/special_product_cust/${segment}/${custId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching special product customer pricing (rootsales):', error);
        throw error;
    }
};

export const getRootsalesCustomerData = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${rootsalesBase(baseUrl)}/api/rootsales/get_customer_data/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching customer data (rootsales):', error);
        throw error;
    }
};

export const uploadRootsalesImage = async (formData, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/upload_image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data;
    } catch (error) {
        console.error('Error uploading image (rootsales):', error);
        throw error;
    }
};

export const getRootsalesRefInformation = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/get_ref_inforamtion`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ref information (rootsales):', error);
        throw error;
    }
};

export const getRootsalesPaymentCallback = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${rootsalesBase(baseUrl)}/api/rootsales/get_payment_callback`, payload);
        return response.data;
    } catch (error) {
        console.error('Error posting payment callback (rootsales):', error);
        throw error;
    }
};

// ─── Santhe App Endpoints ───────────────────────────────────────────────────
// Same shape as Vikraya/Rootsales: Auth/billing-sync endpoints hit the client's
// own DB (client_creation.base_url), master-data endpoints pull from the shared
// master DB (client_creation.server_url) — same split as GS Billing.
const santheBase = (baseUrl) => (baseUrl ? normalizeBaseUrl(baseUrl) : getSalesReactBaseUrl());

export const santheLoginAuthenticate = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/login_authenticate`, payload);
        return response.data;
    } catch (error) {
        console.error('Error authenticating (santhe):', error);
        throw error;
    }
};

export const getSantheBillDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/get_billdetails/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching bill details (santhe):', error);
        throw error;
    }
};

export const syncSantheVikrayaData = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/vikrayadata_synching/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error synching vikraya data (santhe):', error);
        throw error;
    }
};

export const sendSantheSms = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/send_sms/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending SMS (santhe):', error);
        throw error;
    }
};

export const sendSantheSalesTrackingRecords = async (segment, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/sales_tracking_records/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error sending sales tracking records (santhe):', error);
        throw error;
    }
};

export const getSantheClientCountUpdation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/get_client_countupdation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client count updation (santhe):', error);
        throw error;
    }
};

export const getSantheApplicationSetting = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_application_setting/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching application setting (santhe):', error);
        throw error;
    }
};

export const callbackSantheApplicationSetting = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_application_setting/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging application setting (santhe):', error);
        throw error;
    }
};

export const getSantheMastersCreation = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_masters_creation/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching masters creation (santhe):', error);
        throw error;
    }
};

export const callbackSantheMastersCreation = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_masters_creation/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging masters creation (santhe):', error);
        throw error;
    }
};

export const getSantheCreditCustomer = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_credit_customer/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit customer (santhe):', error);
        throw error;
    }
};

export const callbackSantheCreditCustomer = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_credit_customer/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging credit customer (santhe):', error);
        throw error;
    }
};

export const getSantheOutletDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_outlet_deatils/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching outlet details (santhe):', error);
        throw error;
    }
};

export const callbackSantheOutletDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_outlet_deatils/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging outlet details (santhe):', error);
        throw error;
    }
};

export const getSantheProductCategory = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_product_category/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product category (santhe):', error);
        throw error;
    }
};

export const callbackSantheProductCategory = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_product_category/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product category (santhe):', error);
        throw error;
    }
};

export const getSantheProduct = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_product/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching product (santhe):', error);
        throw error;
    }
};

export const callbackSantheProduct = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_product/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging product (santhe):', error);
        throw error;
    }
};

export const getSantheUsers = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_users/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching users (santhe):', error);
        throw error;
    }
};

export const callbackSantheUsers = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_users/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging users (santhe):', error);
        throw error;
    }
};

export const getSanthePrinterDetails = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_printer_details/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching printer details (santhe):', error);
        throw error;
    }
};

export const callbackSanthePrinterDetails = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_printer_details/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging printer details (santhe):', error);
        throw error;
    }
};

export const getSantheNotifications = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/Get_notifications/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching notifications (santhe):', error);
        throw error;
    }
};

export const callbackSantheNotifications = async (segment, payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/callback_notifications/${segment}`, payload);
        return response.data;
    } catch (error) {
        console.error('Error acknowledging notifications (santhe):', error);
        throw error;
    }
};

export const getSantheCreditReport = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/get_credit_report/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching credit report (santhe):', error);
        throw error;
    }
};

export const getSantheLastBillDetails = async (segment, custId, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/get_lastbill_details/${segment}/${custId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching last bill details (santhe):', error);
        throw error;
    }
};

export const getSantheItemsReport = async (segment, userId, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/get_items_report/${segment}/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching items report (santhe):', error);
        throw error;
    }
};

export const getSantheSpecialProductCust = async (segment, custId, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/special_product_cust/${segment}/${custId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching special product customer pricing (santhe):', error);
        throw error;
    }
};

export const getSantheCustomerData = async (segment, baseUrl) => {
    try {
        const response = await axios.get(`${santheBase(baseUrl)}/api/santhe/get_customer_data/${segment}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching customer data (santhe):', error);
        throw error;
    }
};

export const uploadSantheImage = async (formData, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/upload_image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data;
    } catch (error) {
        console.error('Error uploading image (santhe):', error);
        throw error;
    }
};

export const getSantheRefInformation = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/get_ref_inforamtion`, payload);
        return response.data;
    } catch (error) {
        console.error('Error fetching ref information (santhe):', error);
        throw error;
    }
};

export const getSanthePaymentCallback = async (payload, baseUrl) => {
    try {
        const response = await axios.post(`${santheBase(baseUrl)}/api/santhe/get_payment_callback`, payload);
        return response.data;
    } catch (error) {
        console.error('Error posting payment callback (santhe):', error);
        throw error;
    }
};

// ─── Generic API host probe (used by the unified API Endpoints tester) ────────
// Same reachability check as probeGsbillingHost, but takes a fully-formed probe
// URL so one function can serve every module (GS Billing / Vikraya / Rootsales /
// Santhe) instead of duplicating the probe per module.
export const probeApiHost = async (probeUrl) => {
    const startedAt = Date.now();
    try {
        const response = await axios.get(probeUrl, { timeout: 10000 });
        return { ok: response.status === 200 && response.data !== null && typeof response.data === 'object', statusCode: response.status, duration: Date.now() - startedAt };
    } catch (error) {
        const duration = Date.now() - startedAt;
        if (error.response?.status) return { ok: false, statusCode: error.response.status, duration, error: error.message };
        if (error.code === 'ECONNABORTED') return { ok: false, statusCode: 0, duration, timedOut: true, error: error.message };
        const blocked = await hostAnswersOpaquely(probeUrl);
        return {
            ok: false,
            statusCode: 0,
            duration,
            blocked,
            error: blocked ? `${error.message} — the host answered but the browser blocked the response (CORS). Add this page's origin to that deployment's Config/Cors.php allowedOrigins.` : error.message
        };
    }
};

// ─── Client Reports (client → branches → users tree) ──────────────────────────
// Read-only, no request body and no auth filter; both endpoints return Active
// rows only. Same dynamic base URL resolution as the other diagnostics pages.
export const getClientReportsList = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/client_reports/list`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client reports list:', error);
        throw error;
    }
};

export const getClientReportById = async (clientId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/client_reports/list/${clientId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching client report:', error);
        throw error;
    }
};

// ────────────────────────────────────────────────────────────────
// Tax Invoice — GravitySoft's own AMC/domain/SSL invoices to its clients
// ────────────────────────────────────────────────────────────────

// Seller master (company_settings) — CRUD, multi-row
export const getTaxInvoiceSellers = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/sellers`);
        return response.data;
    } catch (error) {
        console.error('Error fetching sellers:', error);
        throw error;
    }
};

export const getDefaultTaxInvoiceSeller = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/sellers/default`);
        return response.data;
    } catch (error) {
        console.error('Error fetching default seller:', error);
        throw error;
    }
};

export const getTaxInvoiceSellerById = async (settingId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/sellers/${settingId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching seller:', error);
        throw error;
    }
};

export const saveTaxInvoiceSeller = async (payload) => {
    try {
        const endpoint = payload.setting_id ? `${getSalesReactBaseUrl()}/api/taxinvoice/sellers/${payload.setting_id}` : `${getSalesReactBaseUrl()}/api/taxinvoice/sellers`;
        const response = await axios.post(endpoint, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving seller:', error);
        throw error;
    }
};

export const deleteTaxInvoiceSeller = async (settingId) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/taxinvoice/sellers/${settingId}/delete`, {});
        return response.data;
    } catch (error) {
        console.error('Error deleting seller:', error);
        throw error;
    }
};

// Buyer master — CRUD
export const getTaxInvoiceBuyers = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/buyers`);
        return response.data;
    } catch (error) {
        console.error('Error fetching buyers:', error);
        throw error;
    }
};

export const getTaxInvoiceBuyerById = async (buyerId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/buyers/${buyerId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching buyer:', error);
        throw error;
    }
};

export const saveTaxInvoiceBuyer = async (payload) => {
    try {
        const endpoint = payload.buyer_id ? `${getSalesReactBaseUrl()}/api/taxinvoice/buyers/${payload.buyer_id}` : `${getSalesReactBaseUrl()}/api/taxinvoice/buyers`;
        const response = await axios.post(endpoint, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving buyer:', error);
        throw error;
    }
};

export const deleteTaxInvoiceBuyer = async (buyerId) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/taxinvoice/buyers/${buyerId}/delete`, {});
        return response.data;
    } catch (error) {
        console.error('Error deleting buyer:', error);
        throw error;
    }
};

export const getNextTaxInvoiceNo = async () => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/next_invoice_no`);
        return response.data;
    } catch (error) {
        console.error('Error fetching next invoice number:', error);
        throw error;
    }
};

export const getTaxInvoices = async (params = {}) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/list`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching tax invoices:', error);
        throw error;
    }
};

export const getTaxInvoiceById = async (invoiceId) => {
    try {
        const response = await axios.get(`${getSalesReactBaseUrl()}/api/taxinvoice/${invoiceId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching tax invoice:', error);
        throw error;
    }
};

export const saveTaxInvoice = async (payload) => {
    try {
        const endpoint = payload.invoice_id ? `${getSalesReactBaseUrl()}/api/taxinvoice/update/${payload.invoice_id}` : `${getSalesReactBaseUrl()}/api/taxinvoice/save`;
        const response = await axios.post(endpoint, payload);
        return response.data;
    } catch (error) {
        console.error('Error saving tax invoice:', error);
        throw error;
    }
};

export const updateTaxInvoiceStatus = async (invoiceId, status) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/taxinvoice/update_status/${invoiceId}`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating tax invoice status:', error);
        throw error;
    }
};

export const deleteTaxInvoice = async (invoiceId) => {
    try {
        const response = await axios.post(`${getSalesReactBaseUrl()}/api/taxinvoice/delete/${invoiceId}`, {});
        return response.data;
    } catch (error) {
        console.error('Error deleting tax invoice:', error);
        throw error;
    }
};

export default {
    getUserMenu,
    loginReact,
    logoutReact,
    getBillFilterHeader,
    getBranches,
    // getBillList,
    filterBillsList,
    getCreditCustomers,
    getClientsList,
    exportCounterProducts,
    exportCounterCategories,
    exportCounterItemReport,
    exportCounterCategoryReport,
    exportOrdersProducts,
    exportOrdersCategories,
    exportOrdersCustomersProducts,
    exportOrdersItemReport,
    exportOrdersCategoryReport,
    exportOrdersCustomerReport,
    formatDateForApi,
    getCategoryReportByDateRange,
    getCategoryReportHourly,
    getCategoryReportTaxwise,
    getCategoryReportTopBottomSelling,
    getReceiptDetails,
    cancelBill,
    updateBillPaymentMode,
    settleCreditBill,
    getInstitutionalFilter,
    insertInstitutionalOrder,
    getOrderById,
    updateOrderCart,
    getInstituteList,
    filterInstituteList,
    // getDispatchDetails,
    updateDispatchStatus,
    getVoucherExpenses,
    createVoucher,
    getVouchers,
    updateVoucher,
    resolveUploadUrl,
    createExpense,
    getExpenses,
    updateExpense,
    clearDataByDateRange,
    getcategorymasters,
    getproductcategory,
    saveProductCategory,
    updateProductCategory,
    updateProductCategoryStatus,
    getNotifications,
    getActiveNotifications,
    getNotificationsByBranch,
    getActiveNotificationsByBranch,
    addNotification,
    updateNotification,
    saveNotification,
    getNotificationReadStatus,
    getUserNotifications,
    markNotificationRead,
    markAllNotificationsRead,
    getApkBackups,
    saveApkBackup,
    shareApkBackup,
    getApkDownloadUrl,
    getModuleGroups,
    getModuleMenus,
    getUserModules,
    getCompanyDetails,
    saveCompany,
    deleteCompany,
    getLocationDetails,
    saveLocation,
    deleteLocation,
    getServerMastersList,
    saveServerMaster,
    sendSmsTest,
    provisionApplicationSettings,
    getProductList,
    saveProduct,
    updateProduct,
    bulkStatusUpdateProduct,
    bulkProductStatusChange,
    bulkPromoteProducts,
    bulkImportProducts,
    bulkTokenUpdate,
    bulkDiscountUpdate,
    bulkPriceChange,
    getOdcHeaderLegacy,
    updateOdcDispatchStatus,
    dispatchOdcOrderAlt,
    getOdcProducts,
    getOdcInstituteListBills,
    filterOdcInstituteBills,
    getInstituteListBills,
    filterInstituteBills,
    tokenDisplayLogin,
    getTokens,
    getParcelTokens,
    callbackTokens,
    systemDisplayApi,
    systemDisplayApi2,
    callbackParcelTokens,
    callSocketSystem,
    callParcelAuth,
    getAvailableDates,
    deleteCounterData,
    processCounterUpdate,
    getTaxInvoiceSellers,
    getDefaultTaxInvoiceSeller,
    getTaxInvoiceSellerById,
    saveTaxInvoiceSeller,
    deleteTaxInvoiceSeller,
    getTaxInvoiceBuyers,
    getTaxInvoiceBuyerById,
    saveTaxInvoiceBuyer,
    deleteTaxInvoiceBuyer,
    getNextTaxInvoiceNo,
    getTaxInvoices,
    getTaxInvoiceById,
    saveTaxInvoice,
    updateTaxInvoiceStatus,
    deleteTaxInvoice
};
